<template>
  <div class="input-field" :class="{ focusing: isFocusing }">
    <input
      ref="field"
      v-model="txtValue"
      v-focus="autofocus"
      :class="[
        {
          'input-field__bolder': !hideBolder,
        },
        inputClass,
      ]"
      :type="type"
      :placeholder="placeholder"
      :autocomplete="autocomplete ? true : 'new-password'"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :maxlength="maxLength"
      @mouseover="onHover"
      @mouseleave="onLeave"
      @focus="onFocus"
      @blur="onBlur"
      @input="onInput"
      @keyup="onKeyup" />

    <div class="actions">
      <i
        v-if="clear && txtValue && txtValue.length && !disabled"
        class="icon icon-cancel input-field-clear d-flex align-items-center"
        @click.stop="onClear"></i>
    </div>

    <div v-if="errorMessageText.length" class="d-flex align-items-center input-field__error">
      <svg width="14" height="14" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="9.5" r="8.75" fill="#F44336" />
        <path
          d="M9.83333 5.33333C9.83333 4.8731 9.46024 4.5 9 4.5C8.53976 4.5 8.16667 4.8731 8.16667 5.33333V9.91667C8.16667 10.3769 8.53976 10.75 9 10.75C9.46024 10.75 9.83333 10.3769 9.83333 9.91667V5.33333Z"
          fill="white" />
        <path
          d="M7.75 13.25C7.75 12.5596 8.30964 12 9 12C9.69036 12 10.25 12.5596 10.25 13.25C10.25 13.9404 9.69036 14.5 9 14.5C8.30964 14.5 7.75 13.9404 7.75 13.25Z"
          fill="white" />
      </svg>
      <p class="typo-p12 text-danger ml-half">
        {{ errorMessageText }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InputField',
  props: {
    value: {
      type: String,
      default: '',
    },
    clear: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hideBolder: {
      type: Boolean,
      default: false,
    },
    inputClass: {
      type: String,
      default: 'input-field__input',
    },
    maxLength: {
      type: [Number, Boolean],
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    disallowBlank: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value', 'clear', 'focus', 'blur', 'hover', 'leave', 'input', 'enter', 'keyup'],
  data() {
    return {
      txtValue: '',
      isFocusing: false,
    };
  },
  computed: {
    errorMessageText() {
      if (this.disallowBlank && this.txtValue.length == 0) {
        return 'Please fill out this field';
      }

      if (this.maxLength && this.txtValue.length > this.maxLength) {
        return 'Error, please check again';
      }

      if (this.errorMessage.length) {
        return this.errorMessage;
      }

      return '';
    },
  },
  watch: {
    value(value) {
      this.txtValue = value;
    },
  },
  mounted() {
    this.txtValue = this.value;
  },
  methods: {
    setFocus(position) {
      this.$refs.field.focus();
      this.isFocusing = true;

      if (position) {
        window.setTimeout(() => {
          this.$refs.field.setSelectionRange(position, position);
        }, 0);
      }
    },

    getOffset() {
      if (this.$refs.field) {
        return this.$refs.field.getBoundingClientRect();
      }

      return {};
    },
    onClear() {
      this.$refs.field.focus();
      this.$emit('update:value', '');
      this.$emit('clear', '');
    },
    onFocus(e) {
      if (!this.disabled) {
        this.$emit('focus', e);
        this.isFocusing = true;
      }
    },
    onBlur(e) {
      this.$emit('blur', e);
      this.isFocusing = false;
    },
    onHover(e) {
      this.$emit('hover', e);
    },
    onLeave(e) {
      this.$emit('leave', e);
    },
    onInput() {
      this.$emit('update:value', this.txtValue);
    },
    onKeyup(event) {
      if (event.keyCode === 13) {
        this.$emit('enter', this.txtValue);
        return;
      }
      this.$emit('keyup', event);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.input-field {
  display: flex;
  align-self: flex-start;
  position: relative;
  width: 100%;
  transition: all 0.3s ease;

  .margin-bottom {
    margin-bottom: 40px !important;
  }

  &__bolder {
    border: 1px solid $text-dark-high;
  }

  &__input {
    padding: 10px 40px 10px 16px;
    border-radius: $radius;
  }

  input {
    display: flex;
    // flex: 1;
    line-height: 18px;
    // caret-color: black;
    // border-radius: 0;
    width: 100%;
    @include trans-all;

    &:hover {
      background-color: $light1;
      border-color: $text-light-low;
    }

    &:focus {
      border-color: $mono1;
    }

    &::placeholder {
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 17px;
    }
  }

  .actions {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    font-size: 16px;
  }

  .actions {
    right: 0px;
    height: 100%;
    display: flex;
  }

  &-clear {
    // margin-left: 12px;
    padding: 0 16px 0 12px;
    transition: all 0.3s ease;

    &:hover {
      color: $mono3;
    }
  }

  &__error {
    height: 20px;
    position: absolute;
    bottom: -24px;
    left: 0;
  }
}
</style>
